import * as React from "react"
import {
  Alert,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Link,
} from "@mui/material"

import EventIcon from "@mui/icons-material/Event"
import PlaceIcon from "@mui/icons-material/Place"
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber"
import DiscreteSliderLabel from "./TicketEventInfoReservation"
import { TicketFallbackBanner } from "./TicketFallbackBanner"
import { useTranslation } from "react-i18next"

export type TicketAreaOption = {
  codeId: string // o que você vai enviar para a API (areaCodeId)
  name: string // nome exibido
  description?: string // opcional (ex.: "Pista", "VIP", "Camarote")
  disabled?: boolean // opcional (ex.: área indisponível)
}

export type TicketEventInfoProps = {
  eventDate?: Date | null
  eventLocation?: string | null
  eventLocationUrl?: string | null

  areas: TicketAreaOption[]
  selectedAreaCodeId: string

  onChangeArea: (areaCodeId: string) => void

  title?: string // opcional (ex.: "Ingresso")
  locale?: string // opcional (default: idioma do checkout)

  usedFallbackSection?: boolean // opcional (indica se essa opção é resultado de um fallback automático)
  fallbackMessage?: string // opcional (mensagem específica para mostrar no banner quando usedFallbackSection for true)
  requireNominalAtCheckout?: boolean // opcional (indica que o ingresso é nominal e precisa ser nominado após a compra)
}

function formatEventDate(date: Date, locale: string) {
  // Mostra dia da semana, data e hora — ajuste conforme sua necessidade.
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

export const TicketEventInfo: React.FC<TicketEventInfoProps> = ({
  eventDate,
  eventLocation,
  eventLocationUrl,
  areas,
  selectedAreaCodeId,
  onChangeArea,
  title,
  locale,
  usedFallbackSection = false,
  fallbackMessage,
  requireNominalAtCheckout = false,
}) => {
  const { t, i18n } = useTranslation()
  const dateLocale = locale ?? i18n.resolvedLanguage ?? "pt-BR"

  const handleChange = (e: SelectChangeEvent<string>) => {
    onChangeArea(e.target.value)
  }

  const selectedArea = React.useMemo(() => {
    return areas.find((a) => a.codeId === selectedAreaCodeId)
  }, [areas, selectedAreaCodeId])

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, mt: 2 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Box display="flex" alignItems="center" gap={1.2}>
            <ConfirmationNumberIcon fontSize="small" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {title ?? t("ticket.title")}
            </Typography>
          </Box>

          {selectedArea?.name ? (
            <Chip
              size="small"
              label={selectedArea.name}
              sx={{ fontWeight: 600 }}
              variant="outlined"
            />
          ) : null}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Data do evento */}
        <Box display="flex" gap={1.5} alignItems="flex-start" mb={1.5}>
          <EventIcon fontSize="small" sx={{ mt: "2px" }} />
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.2 }}
            >
              {t("ticket.eventDate")}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {eventDate
                ? formatEventDate(new Date(eventDate), dateLocale)
                : t("ticket.toBeDefined")}
            </Typography>
          </Box>
        </Box>

        {/* Local do evento */}
        <Box display="flex" gap={1.5} alignItems="flex-start" mb={2}>
          <PlaceIcon fontSize="small" sx={{ mt: "2px" }} />
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.2 }}
            >
              {t("ticket.location")}
            </Typography>

            {eventLocationUrl ? (
              <Link
                href={eventLocationUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontWeight: 600 }}
              >
                {eventLocation?.trim() || t("ticket.seeOnMap")}
              </Link>
            ) : (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {eventLocation?.trim() || t("ticket.toBeDefined")}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <TicketFallbackBanner
          open={usedFallbackSection}
          variant="warning"
          title={t("ticket.areaChangedTitle")}
          message={
            fallbackMessage ??
            t("ticket.areaChangedMessage")
          }
          actionLabel={t("ticket.chooseAnotherOption")}
          //onAction={() => setOpenTierSelector(true)}
          //onClose={() => setHideBanner(true)}
        />

        <Box height={usedFallbackSection ? 16 : 8} />

        {/* Seleção de área */}
        <FormControl fullWidth size="small">
          <InputLabel id="ticket-area-label">{t("ticket.area")}</InputLabel>
          <Select
            labelId="ticket-area-label"
            value={selectedAreaCodeId}
            label={t("ticket.area")}
            onChange={handleChange}
          >
            {areas.map((area) => (
              <MenuItem
                key={area.codeId}
                value={area.codeId}
                disabled={area.disabled}
              >
                <Box display="flex" flexDirection="column" width="100%">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {area.name}
                  </Typography>
                  {area.description ? (
                    <Typography variant="caption" color="text.secondary">
                      {area.description}
                    </Typography>
                  ) : null}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Dica/estado vazio */}
        {areas.length === 0 ? (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={1}
          >
            {t("ticket.noAreas")}
          </Typography>
        ) : (
          <DiscreteSliderLabel />
        )}

        {!requireNominalAtCheckout && (
          <Alert
            severity="info"
            variant="outlined"
            sx={{ mt: 2, py: 0.5, borderRadius: 2 }}
          >
            {t("ticket.afterPurchaseInfo")}
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
